import React, { useEffect, useRef, useState } from 'react';
import './styles.css';

import Result from '../../merode/checks/Result';
import Message, { MessageType, type MessageTypeValue } from '../../merode/checks/Message';
import type Report from '../../merode/checks/Report';

import { RSAlert, RSButton, RSCheckbox, RSInput } from 'reactsymbols-kit';
import MerlinSelect, { type MerlinSelectOption } from '../MerlinSelect';
import Styles from '../../styles/colors';

import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';

/**
 * reactsymbols-kit's input-family components (`RSInput`, `RSCheckbox`) extend
 * `React.HTMLProps<HTMLInputElement>`, so their declared `onChange` is the
 * unusable INTERSECTION of the DOM `FormEventHandler` with the kit's own
 * value-based callback — no single handler satisfies both. We route the real
 * value callback through this helper, which re-types it as whatever `onChange`
 * shape the target component declares. The cast is the single library-boundary
 * escape for this kit bug; the runtime handler is unchanged.
 *
 * `RSInputChange` / `RSCheckboxChange` are the exact (broken) prop types, read
 * straight off the components so the helper hands back the type each prop wants.
 */
type RSInputChange = React.ComponentProps<typeof RSInput>['onChange'];
type RSCheckboxChange = React.ComponentProps<typeof RSCheckbox>['onChange'];

function asRSChange<T>(handler: (...args: never[]) => void): T {
  return handler as unknown as T;
}

/**
 * A column in the report table.
 *
 * - title
 * - value: function to extract the (string) value from a Message object
 * - filter: does the column needs to be free-text filterable?
 */
interface ReportColumn {
  title: string;
  value: (message: Message) => string;
  filter: boolean;
}

/**
 * The columns in the table.
 *
 * `subject` / `check` are optional on a Message, but a report message always
 * carries them when shown here (the checks attach both). The originals read
 * `.displayString` / `.description` directly; the non-null assertions preserve
 * that. `CheckSubject` is a union and not every member declares
 * `displayString`, so that one access goes through a narrow structural cast.
 */
const columns: Record<'type' | 'subject' | 'check' | 'message', ReportColumn> = {
  type: {
    title: 'Severity',
    value: (message) => message.type,
    filter: false,
  },
  subject: {
    title: 'Element',
    value: (message) => (message.subject as { displayString: string }).displayString,
    filter: true,
  },
  check: {
    title: 'Check',
    value: (message) => message.check!.description,
    filter: true,
  },
  message: {
    title: 'Message',
    value: (message) => message.message,
    filter: true,
  },
};

/**
 * Renders a report. Including filtering and search functionality.
 *
 * props:
 * - scrollContainerRef: react ref for the scroll container
 *
 * The three state fields each get their own useState slot. `onMessageTypeChange`
 * used a functional `setState` to concat/filter `messageTypes`, preserved here
 * with the functional `setMessageTypes` updater.
 */
interface ReportViewProps {
  /** The report to render. */
  report: Report;
  /** Disable scrolling of the body while mounted? */
  disableBodyScroll?: boolean;
  /** Called when the Close button is pressed. */
  onRequestClose?: () => void;
}

// The filterable columns (the text filter targets one of these keys).
type FilterableColumnKey = 'subject' | 'check' | 'message';

export default function ReportView({ report, disableBodyScroll: disableBodyScrollProp, onRequestClose }: ReportViewProps) {
  const [filter, setFilter] = useState('');
  const [filteredColumn, setFilteredColumn] = useState<FilterableColumnKey>('check');
  const [messageTypes, setMessageTypes] = useState<MessageTypeValue[]>([MessageType.WARNING, MessageType.ERROR]);

  // Disabling scroll on the body while visible
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Disable body scroll on mount, re-enable on unmount. The class did this in
  // componentDidMount / componentWillUnmount (after paint), so a plain
  // useEffect matches that timing exactly. The prop is captured at mount via
  // the effect closure; it never changes for a mounted ReportView.
  useEffect(() => {
    // Snapshot the node at mount so the cleanup re-enables scroll on the same
    // element it disabled, even if the ref has since changed (it can't here —
    // the node is stable for a mounted ReportView — but this also silences the
    // ref-in-cleanup hooks lint warning).
    const node = scrollContainerRef.current;
    if (disableBodyScrollProp && node) {
      disableBodyScroll(node);
    }
    return () => {
      if (disableBodyScrollProp && node) {
        enableBodyScroll(node);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Change handlers

  const onMessageTypeChange = (value: MessageTypeValue, checked: boolean) => {
    if (checked) {
      setMessageTypes((prev) => prev.concat([value]));
    } else {
      setMessageTypes((prev) => prev.filter((v) => v !== value));
    }
  };

  // Rendering

  const renderStatus = () => {
    let level, message;
    switch (report.result) {
      case Result.SUCCESSFUL:
        level = 'success';
        message = 'The checks were successful.';
        break;
      case Result.SUCCESSFUL_WITH_WARNING:
        level = 'warning';
        message = 'The checks were successful with warnings.';
        break;
      case Result.FAILED:
      default:
        level = 'danger';
        message = 'The checks failed.';
        break;
    }
    return (
      <RSAlert visible level={level}>
        <p>{message}</p>
      </RSAlert>
    );
  };

  const renderMessage = (message: Message) => {
    let subject = columns.subject.value(message);
    if (subject != null) {
      // Insert some zero-width spaces after dots and _ to indicate word break
      // opportunities
      subject = subject.replace(/([._])/g, '$1​');
    }
    return (
      <tr key={message.id}>
        <td className="type w-[100px] border-t border-black py-[5px] pr-5 pl-0">
          {columns.type.value(message)}
        </td>
        <td className="subject w-[450px] max-w-[calc((100vw-60px-300px)*3/8)] overflow-hidden border-t border-black py-[5px] pr-5 pl-0 hyphens-auto">
          {subject}
        </td>
        <td className="check w-[280px] border-t border-black py-[5px] pr-5 pl-0">
          {columns.check.value(message)}
        </td>
        <td className="message w-[750px] max-w-[calc((100vw-60px-300px)*5/8)] border-t border-black py-[5px] pr-5 pl-0">
          {columns.message.value(message)}
        </td>
      </tr>
    );
  };

  const filteredMessages = () => {
    return report.messages
      .filter((m) => {
        // Filter on message type
        return messageTypes.includes(m.type);
      })
      .filter((m) => {
        // Text-based filter for other columns
        const column = columns[filteredColumn];
        const value = column.value(m);
        return value && value.toLowerCase().includes(filter.toLowerCase());
      });
  };

  // The table with messages
  const renderMessages = () => {
    return (
      <table className="min-w-full border-collapse">
        <thead>
          <tr>
            <th className="pt-[15px] pr-5 pb-[5px] pl-0 text-left font-semibold">{columns.type.title}</th>
            <th className="pt-[15px] pr-5 pb-[5px] pl-0 text-left font-semibold">{columns.subject.title}</th>
            <th className="pt-[15px] pr-5 pb-[5px] pl-0 text-left font-semibold">{columns.check.title}</th>
            <th className="pt-[15px] pr-5 pb-[5px] pl-0 text-left font-semibold">{columns.message.title}</th>
          </tr>
        </thead>
        <tbody>{filteredMessages().map((m) => renderMessage(m))}</tbody>
      </table>
    );
  };

  const renderFilters = () => {
    return (
      <div className="filters flex items-start justify-between border-t border-grey pt-[10px]">
        <div className="search flex-[0_0_80%]">
          <div className="row">
            <div className="col-2">
              <div className="rs-element rs-input-container rs-input">
                <label className="rs-element rs-form-label">Column</label>
                <MerlinSelect
                  name="column"
                  options={Object.entries(columns)
                    .filter(([key, value]) => value.filter)
                    .map(([key, value]) => ({
                      label: value.title,
                      value: key,
                    }))}
                  value={filteredColumn}
                  clearable={false}
                  searchable={false}
                  onChange={(option: MerlinSelectOption | null) =>
                    option != null && setFilteredColumn(option.value as FilterableColumnKey)
                  }
                />
              </div>
            </div>
            <div className="col-2">
              <RSInput
                name="filter"
                label="Filter"
                value={filter}
                // reactsymbols-kit's RSInput extends React.HTMLProps, so its
                // declared `onChange` is the unusable intersection of the DOM
                // FormEventHandler with the kit's value callback. Route the
                // real value handler through `asRSChange` (see top of file).
                onChange={asRSChange<RSInputChange>((value: string) => setFilter(value))}
              />
            </div>
          </div>
          <RSButton
            value="Clear"
            background={Styles.defaultGradient}
            color={Styles.darkTextColor}
            onClick={() => setFilter('')}
          />
        </div>
        <div className="message-type-filters flex-[0_0_20%] pt-[34px] pr-[10px] pb-[10px] pl-[25px]">
          <RSCheckbox
            label={MessageType.INFORMATION}
            value={MessageType.INFORMATION}
            checked={messageTypes.includes(MessageType.INFORMATION)}
            onChange={asRSChange<RSCheckboxChange>(onMessageTypeChange)}
          />
          <RSCheckbox
            label={MessageType.WARNING}
            value={MessageType.WARNING}
            checked={messageTypes.includes(MessageType.WARNING)}
            onChange={asRSChange<RSCheckboxChange>(onMessageTypeChange)}
          />
          <RSCheckbox
            label={MessageType.ERROR}
            value={MessageType.ERROR}
            checked={messageTypes.includes(MessageType.ERROR)}
            onChange={asRSChange<RSCheckboxChange>(onMessageTypeChange)}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="merlin-modal report-view w-[1500px] max-w-[calc(100vw-60px)]">
      <div className="title">Report</div>
      <div className="modal-body">
        <div className="container">
          <div className="row">
            <div className="col">
              <div className="status">{renderStatus()}</div>
            </div>
          </div>
          <div className="row">
            <div className="col">
              <div
                className="messages mt-[15px] h-[300px] overflow-auto"
                ref={disableBodyScrollProp ? scrollContainerRef : undefined}
              >
                {renderMessages()}
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col">{renderFilters()}</div>
          </div>
        </div>
      </div>
      <div className="button-wrapper">
        <RSButton
          value="Close"
          background={Styles.defaultGradient}
          color={Styles.darkTextColor}
          onClick={onRequestClose}
        />
      </div>
    </div>
  );
}
